<template>
    <div>
      <StackedHorizontalBarChart
        :title=chartTitle
        :filterContext="filterContext"
        :dataProviders="dataProviders"
        :contextUpdaters="contextUpdaters"
        :navigationConfig="navigationConfig"
        :chartFontColor="chartFontColor"
        :chartCardHeight="940"
        @updateFilter="handleFilterUpdate"
      />
    </div>
  </template>
  
<script setup>
  import { computed, ref } from 'vue';
  import { useStore } from 'vuex';
  import { useI18n } from 'vue-i18n';
  import StackedHorizontalBarChart from '@/components/charts/base/StackedHorizontalBarChart.vue';
  import { ensureChartUsersTable } from '@/utils/chartUsers';

  
  const store = useStore();
  const { t } = useI18n();
  const filterContext = computed(() => store.state.filterItemsContext);
  const chartFontColor = computed(() => store.getters.chartFontColor);
  const conn = store.getters.getDBConnection;

  const chartName = 'teamUserStackedBarChart';
  const chartTitle = t('teamAndUserBreakdown');

  const selectedTeamId = ref(null);

  // Last drill-down filter query, so a status click keeps the current scope
  let currentDrillQuery = null;

const ensureUsersTable = () => ensureChartUsersTable(conn);


/*const getTeamProfiles = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    WITH items_with_team_profile AS (
        SELECT 
            COALESCE(t.teamProfileId, 'unassigned') AS id,
            COALESCE(t.teamProfileTitle, 'Un-assigned') AS title,
            s.title AS status_title,
            s.color as color,
            s.sort
        FROM ${table} i
        JOIN status_templates s ON i.StatusId = s.id
        LEFT JOIN teams t ON i.AssigneeTeamId = t.id
    )

    SELECT 
        id,
        title,
        status_title,
        color,
        COUNT(*) AS value,
        MIN(sort) AS sort
    FROM items_with_team_profile
    GROUP BY id, title, status_title, color
    ORDER BY title, sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};*/

const getTeams = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
        COALESCE(t.id, 'unassigned') AS id,
        COALESCE(t.name, 'Un-assigned') AS title,
        s.title as status_title,
        s.color as color,
        COUNT(*) AS value,
        MIN(s.sort) AS sort
    FROM ${table} i
    JOIN status_templates s ON i.StatusId = s.id
    LEFT JOIN teams t ON i.AssigneeTeamId = t.id
    GROUP BY COALESCE(t.id, 'unassigned'), COALESCE(t.name, 'Un-assigned'), s.title, s.color
    ORDER BY COALESCE(t.name, 'Un-assigned'), sort, status_title
  `;
  
  return executeAndProcessQuery(query);
};
   
const getUsers = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  await ensureUsersTable();
  const query = `
        SELECT
        COALESCE(u.id, 'unassigned') AS id,
        COALESCE(u.displayName, 'Un-assigned') AS title,
        s.title as status_title,
        s.color as color,
        COUNT(*) AS value,
        MIN(s.sort) AS sort
    FROM ${table} i
    JOIN status_templates s ON i.StatusId = s.id
    LEFT JOIN chart_users u ON i.AssigneeUserIds LIKE '%"' || u.id || '"%'
    GROUP BY COALESCE(u.id, 'unassigned'), COALESCE(u.displayName, 'Un-assigned'), s.title, s.color
    ORDER BY COALESCE(u.displayName, 'Un-assigned'), sort, status_title
  `;

  return executeAndProcessQuery(query);
};

const getStatusData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT
      s.title AS id,
      s.title AS title,
      s.title as status_title,
      s.color as color,
      SUM(COUNT(*)) OVER (PARTITION BY s.title, s.color) AS value,
      MIN(s.sort) AS sort
    FROM ${table} i
    JOIN status_templates s ON i.StatusId = s.id
    GROUP BY s.title, s.color, s.sort
    ORDER BY s.sort, s.title
  `;

  return executeAndProcessQuery(query);
};


/*const getTeamProfileFilterContext = async (teamProfileId) => {

  if (teamProfileId == 'unassigned') {
    const query = `
      SELECT DISTINCT i.SubItemId
      FROM items i
      Where i.AssigneeTeamId = ''
    `;
    await updateFilteredItemsContext(query);

  } else {
    const query = `
      SELECT DISTINCT i.SubItemId
      FROM items i
      JOIN teams t ON i.AssigneeTeamId = t.id
      WHERE t.teamProfileId = '${teamProfileId}'
    `;
    await updateFilteredItemsContext(query);
  }
};*/

// Filter through the same teams/users joins the display queries use, so id type,
// casing, and un-assigned semantics always match what the user clicked on
const teamCondition = (teamId) =>
  teamId === 'unassigned' ? 't.id IS NULL' : `CAST(t.id AS VARCHAR) = '${teamId}'`;

const userCondition = (userId) =>
  userId === 'unassigned' ? 'u.id IS NULL' : `u.id = '${userId}'`;

const getTeamFilterContext = async (teamId) => {
  selectedTeamId.value = teamId;

  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    LEFT JOIN teams t ON i.AssigneeTeamId = t.id
    WHERE ${teamCondition(teamId)}
  `;
  currentDrillQuery = query;
  await updateFilteredItemsContext(query);
};

const getUserFilterContext = async (userId) => {
  await ensureUsersTable();
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    LEFT JOIN teams t ON i.AssigneeTeamId = t.id
    LEFT JOIN chart_users u ON i.AssigneeUserIds LIKE '%"' || u.id || '"%'
    WHERE ${teamCondition(selectedTeamId.value)} AND ${userCondition(userId)}
  `;
  currentDrillQuery = query;
  await updateFilteredItemsContext(query);
};

const getStatusFilterContext = async (statusTitle) => {
  // Filter within the current drill-down scope when one is active
  const query = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    JOIN status_templates s ON i.StatusId = s.id
    WHERE s.title = '${statusTitle}'
    ${currentDrillQuery ? `AND i.SubItemId IN (${currentDrillQuery})` : ''}
  `;
  await updateFilteredItemsContext(query);
};

const resetFilterContext = async () => {
    selectedTeamId.value = null;
    currentDrillQuery = null;
    await filterContext.value.removeFilter(chartName);
}

  const dataProviders = {
    //teamprofiles: getTeamProfiles,
    teams: getTeams,
    users: getUsers,
    status: getStatusData
  };

  const contextUpdaters = {
    //teamprofiles: getTeamProfileFilterContext,
    teams: getTeamFilterContext,
    users: getUserFilterContext,
    status: getStatusFilterContext,
    reset: resetFilterContext /*required*/
  };

  const navigationConfig = [
    //{ level: 'teamprofiles', titleKey: t('teamProfile') },
    { level: 'teams', titleKey: 'teams' },
    { level: 'users', titleKey: 'users' },
    { level: 'status', titleKey: 'status' }
  ];


  const updateFilteredItemsContext = async (query) => {
    await filterContext.value.updateFilterWithQuery(chartName, query);
  };

  // Shared data processing utility function
const processChartData = (rows) => {
  const groupedData = {};
  
  rows.forEach(row => {
    const id = row['id'];
    const title = row['title'];
    const parentId = row['parentId'] ? row['parentId'] : null;
    const type = row['type'] ? row['type'] : null
    
    if (!groupedData[id]) {
      groupedData[id] = {
        name: title,
        id: id,
        parentId: parentId,
        type: type,
        children: []
      };
    }
    
    // Find if this color already exists in the children
    const existingColor = groupedData[id].children.find(
      child => child.color === row.color
    );
    
    if (existingColor) {
      existingColor.value += row.value;
      // Store the status names by color
      if (!existingColor.statusCounts) {
        existingColor.statusCounts = {};
      }
      existingColor.statusCounts[row.status_title] = row.value;
    } else {
      groupedData[id].children.push({
        color: row.color,
        value: row.value,
        statusName: row.status_title,
        sort: row.sort,
        statusCounts: { [row.status_title]: row.value }
      });
    }
  });
  
  return Object.values(groupedData).map(item => ({
    ...item,
    children: item.children.sort((a, b) => a.sort - b.sort)
  }));
};

// Generic query execution function
const executeAndProcessQuery = async (query, idField, titleField) => {
  const result = await conn.query(query);
  const rows = result.toArray().map((row) => {
    const jsonRow = row.toJSON();
    return {
      ...jsonRow,
      value: Number(jsonRow.value)
    };
  });
  
  return processChartData(rows, idField, titleField);
};

  </script>